import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Send, ArrowLeft, UserPlus, Circle, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const AUD_LABEL = { internal: "Internal", partner: "Partner", customer: "Customer", all: "Everyone" };
const AVATAR_TONES = ["bg-emerald-600", "bg-blue-600", "bg-violet-600", "bg-rose-500", "bg-amber-600", "bg-cyan-600"];
const tone = (n) => { let h = 0; for (const c of n || "") h = (h + c.charCodeAt(0)) % AVATAR_TONES.length; return AVATAR_TONES[h]; };
const inits = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const ago = (s) => { if (!s) return ""; const m = Math.floor((Date.now() - new Date(s)) / 60000); if (m < 1) return "now"; if (m < 60) return `${m}m`; if (m < 1440) return `${Math.floor(m / 60)}h`; return new Date(s).toLocaleDateString([], { month: "short", day: "numeric" }); };

const audOptions = (role) => role === "staff"
  ? [{ v: "internal", l: "Internal (staff only)" }, { v: "partner", l: "Partner" }, { v: "customer", l: "Customer" }, { v: "all", l: "Everyone" }]
  : role === "partner" ? [{ v: "partner", l: "Provider" }, { v: "all", l: "Everyone (Provider + Customer)" }]
    : [{ v: "customer", l: "Provider" }, { v: "all", l: "Everyone" }];

export const MessageCenter = ({ onCountChange }) => {
  const { user } = useAuth();
  const role = user?.base_role;
  const isStaff = role === "staff";
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const tabs = isStaff
    ? [["all", "All"], ["assigned", "Assigned to me"], ["unassigned", "Unassigned"], ["sent", "Sent"], ["unread", "Unread"]]
    : [["all", "All"], ["sent", "Sent"], ["unread", "Unread"]];

  const load = () => api.get(`/message-threads?tab=${tab}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setThreads(r.data)).catch(() => setThreads([]));
  useEffect(() => { if (!user?.user_id) return; load(); /* eslint-disable-next-line */ }, [tab, q, user]);
  const refreshCount = () => api.get("/messages/unread-count").then((r) => onCountChange?.(r.data.count)).catch(() => {});

  const open = (tid) => api.get(`/message-threads/${tid}`).then((r) => { setActive(r.data); load(); refreshCount(); }).catch(() => toast.error("Failed to open"));

  if (active) return <ThreadView thread={active} setThread={setActive} onBack={() => { setActive(null); load(); refreshCount(); }} isStaff={isStaff} />;

  return (
    <div data-testid="message-center">
      <div className="mb-4 flex items-center justify-between">
        <div><h1 className="text-xl font-semibold text-slate-800">Messages</h1><p className="text-sm text-slate-400">Conversations across your projects and team.</p></div>
        <Button data-testid="new-message-button" onClick={() => setComposeOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> New message</Button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`msgtab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${tab === k ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <div className="relative ml-auto w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="message-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations..." className="pl-9" />
        </div>
      </div>

      <Card className="border-slate-200 divide-y divide-slate-50">
        {threads.length === 0 ? <div className="px-5 py-12 text-center text-sm text-slate-400" data-testid="messages-empty">No conversations.</div> :
          threads.map((t) => (
            <button key={t.thread_id} data-testid={`thread-row-${t.thread_id}`} onClick={() => open(t.thread_id)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50/70">
              <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${tone(t.last_message_by)}`}>{inits(t.last_message_by)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {t.unread && <Circle className="h-2 w-2 shrink-0 fill-primary text-primary" data-testid={`unread-${t.thread_id}`} />}
                  <span className={`truncate text-sm ${t.unread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>{t.subject}</span>
                  <span className="ml-auto shrink-0 text-xs text-slate-400">{ago(t.last_message_at)}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="truncate text-xs text-slate-500"><span className="font-medium">{t.last_message_by}:</span> {t.last_message_preview}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {t.project_name && <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">{t.project_name}</span>}
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{AUD_LABEL[t.audience]}</span>
                  {isStaff && t.assigned_to_name && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">@ {t.assigned_to_name}</span>}
                </div>
              </div>
            </button>
          ))}
      </Card>

      {composeOpen && <ComposeDialog role={role} onClose={() => setComposeOpen(false)} onSent={(t) => { setComposeOpen(false); open(t.thread_id); }} />}
    </div>
  );
};

const ThreadView = ({ thread, setThread, onBack, isStaff }) => {
  const [body, setBody] = useState("");
  const [staffList, setStaffList] = useState([]);
  useEffect(() => { if (isStaff) api.get("/users").then((r) => setStaffList((r.data || []).filter((u) => u.base_role === "staff"))).catch(() => {}); }, [isStaff]);

  const send = async () => {
    if (!body.trim()) return;
    try { const { data } = await api.post(`/message-threads/${thread.thread_id}/messages`, { body }); setThread(data); setBody(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const assign = async (uid) => {
    try { await api.put(`/message-threads/${thread.thread_id}/assign`, { assigned_to: uid === "none" ? null : uid }); const { data } = await api.get(`/message-threads/${thread.thread_id}`); setThread(data); toast.success("Updated"); }
    catch (e) { toast.error("Failed to assign"); }
  };
  const setWatchers = async (ids) => {
    try { const { data } = await api.put(`/message-threads/${thread.thread_id}/watchers`, { watchers: ids }); setThread({ ...thread, ...data }); }
    catch (e) { toast.error("Failed to update watchers"); }
  };
  const watchers = thread.watchers || [];
  const watcherNames = thread.watcher_names || [];
  const addable = staffList.filter((u) => !watchers.includes(u.user_id));

  return (
    <div data-testid="thread-view">
      <Button variant="ghost" size="sm" data-testid="thread-back" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Back to messages</Button>
      <Card className="mt-3 border-slate-200">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800">{thread.subject}</h2>
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400">
              {thread.project_name && <span className="rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">{thread.project_name}</span>}
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500">{AUD_LABEL[thread.audience]}</span>
            </div>
          </div>
          {isStaff && thread.can_assign && (
            <div className="ml-auto flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-slate-400" />
              <Select value={thread.assigned_to || "none"} onValueChange={assign}>
                <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="thread-assign"><SelectValue placeholder="Assign" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{staffList.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        {isStaff && (
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/50 px-5 py-2.5" data-testid="thread-watchers">
            <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><Eye className="h-3.5 w-3.5" /> CC / Watchers:</span>
            {watcherNames.length === 0 && <span className="text-xs text-slate-400">None</span>}
            {watcherNames.map((w) => (
              <span key={w.user_id} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600" data-testid={`watcher-${w.user_id}`}>
                {w.name}
                <button onClick={() => setWatchers(watchers.filter((x) => x !== w.user_id))} className="text-slate-300 hover:text-red-500" data-testid={`watcher-remove-${w.user_id}`}>×</button>
              </span>
            ))}
            {addable.length > 0 && (
              <Select value="" onValueChange={(uid) => setWatchers([...watchers, uid])}>
                <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="watcher-add"><SelectValue placeholder="+ Add watcher" /></SelectTrigger>
                <SelectContent>{addable.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        )}

        <div className="max-h-[50vh] space-y-3 overflow-y-auto px-5 py-4" data-testid="thread-messages">
          {thread.messages.map((m) => (
            <div key={m.message_id} className={`flex gap-2.5 ${m.is_me ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.message_id}`}>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${tone(m.author_name)}`}>{inits(m.author_name)}</span>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${m.is_me ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-800"}`}>
                <div className={`mb-0.5 flex items-center gap-2 text-[11px] ${m.is_me ? "text-primary-foreground/80" : "text-slate-400"}`}><span className="font-medium">{m.author_name}</span><span>· {ago(m.created_at)}</span></div>
                <p className="whitespace-pre-wrap text-sm">{m.body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 px-5 py-3">
          <div className="flex items-end gap-2">
            <Textarea data-testid="reply-input" rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a reply…" className="flex-1" />
            <Button data-testid="reply-send" onClick={send} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

const ComposeDialog = ({ role, onClose, onSent }) => {
  const [form, setForm] = useState({ subject: "", body: "", audience: audOptions(role)[0].v, partner_org_id: "", customer_org_id: "", project_id: "" });
  const [orgs, setOrgs] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (role !== "customer") api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, [role]);
  const sides = { internal: [], partner: ["partner"], customer: ["customer"], all: ["partner", "customer"] }[form.audience] || [];
  const partners = orgs.filter((o) => o.type === "partner");
  const customers = orgs.filter((o) => o.type === "customer");

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and message are required"); return; }
    const payload = { subject: form.subject, body: form.body, audience: form.audience, project_id: form.project_id || null };
    if (role === "staff") { payload.partner_org_id = form.partner_org_id || null; payload.customer_org_id = form.customer_org_id || null; }
    if (role === "partner" && form.audience === "all") payload.customer_org_id = form.customer_org_id || null;
    try { const { data } = await api.post("/message-threads", payload); toast.success("Message sent"); onSent(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New message</DialogTitle><DialogDescription>Start a conversation. Staff identities are white-labelled for partners and customers.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Subject</Label><Input data-testid="compose-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="What's this about?" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Audience</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="compose-audience"><SelectValue /></SelectTrigger>
                <SelectContent>{audOptions(role).map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Link to project (optional)</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="compose-project"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {role === "staff" && sides.includes("partner") && !form.project_id && (
            <div className="space-y-1.5"><Label>Partner</Label>
              <Select value={form.partner_org_id} onValueChange={(v) => setForm({ ...form, partner_org_id: v })}>
                <SelectTrigger data-testid="compose-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>{partners.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {((role === "staff") || (role === "partner")) && sides.includes("customer") && !form.project_id && (
            <div className="space-y-1.5"><Label>Customer</Label>
              <Select value={form.customer_org_id} onValueChange={(v) => setForm({ ...form, customer_org_id: v })}>
                <SelectTrigger data-testid="compose-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Message</Label><Textarea data-testid="compose-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Write your message…" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="compose-send" onClick={submit} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Send className="h-4 w-4" /> Send</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
